/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { ReactNode } from 'react';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { CTAType, useAvroTracking } from '../../../observability';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../../sections/types';
import { ArrowLink } from '../../ArrowLink/ArrowLink';

export type CustomCTAFilledProps = {
  heading: string;
  description?: RichTextHTMLString;
  link?: {
    text?: string;
    url?: string;
  };
  backgroundColor?: 'lavender' | 'peach' | 'bolt' | 'sky' | 'cream';
  children?: ReactNode;
};

const Styles = labelStyles('CustomCTAFilled', {
  root: css({
    padding: '32px 20px',

    [MEDIA_MIN_WIDTH.small]: {
      padding: '48px 20px',
    },
  }),

  container: (backgroundColor: CustomCTAFilledProps['backgroundColor']) =>
    css({
      maxWidth: '1170px',
      margin: '0 auto',
      padding: '40px 20px',
      boxSizing: 'border-box',
      display: 'grid',
      gridTemplateColumns: '1fr',
      alignItems: 'center',
      gap: '22px',
      borderRadius: '20px',
      backgroundColor: Colors[backgroundColor ?? 'peach'],

      [MEDIA_MIN_WIDTH.small]: {
        padding: '30px',
        gridTemplateColumns: '320px 1fr',
        gap: '32px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '48px 64px 48px 48px',
        gridTemplateColumns: '470px 1fr',
        gap: '64px',
      },

      [MEDIA_MIN_WIDTH.extraLarge]: {
        padding: '62px 80px 62px 62px',
        gap: '128px',
      },
    }),

  heading: css({
    fontFamily: Typography.heading.fontFamily,
    fontSize: '36px',
    fontWeight: 600,
    lineHeight: 0.85,
    letterSpacing: '1px',
    color: Colors.earth,
    maxWidth: '576px',

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '40px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '56px',
    },

    [MEDIA_MIN_WIDTH.extraLarge]: {
      fontSize: '70px',
    },
  }),

  description: css({
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: 1.33,
    letterSpacing: '-0.5px',
    color: Colors.earth,
    maxWidth: '480px',
    marginTop: '16px',

    [MEDIA_MIN_WIDTH.small]: {
      marginTop: '20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '20px',
    },
  }),

  link: css({
    marginTop: '16px',

    [MEDIA_MIN_WIDTH.small]: {
      marginTop: '20px',
    },
  }),
});

export function CustomCTAFilled(props: CustomCTAFilledProps) {
  const { emitCTAClickEvent } = useAvroTracking();

  return (
    <div css={Styles.root}>
      <div css={Styles.container(props.backgroundColor)}>
        <div>{props.children}</div>
        <div>
          <h2 css={Styles.heading}>{props.heading}</h2>
          {props.description && (
            <div css={Styles.description}>
              <RichTextHtml html={props.description} />
            </div>
          )}
          {props.link?.text && props.link?.url && (
            <div css={Styles.link}>
              <ArrowLink
                href={props.link.url}
                onClick={() => {
                  emitCTAClickEvent({
                    text: props.link?.text ?? '',
                    href: props.link?.url ?? '',
                    section: 'CustomCTAFilled',
                    type: CTAType.cta_link,
                  });
                }}
              >
                {props.link.text}
              </ArrowLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
